import React from 'react';
import V2Button from '../../../components/button/v2_Button';
import V2Icon from '../../../components/icon/v2_Icon';
import { useV2Theme } from '../../../themecontrols/v2_ThemeContext';
import cloobotXLogoDark from '../../../assets/Cloobot X Logo.svg';
import cloobotXLogoLight from '../../../assets/Cloobot X Logo__Light.png';
import './V2LoginCard.css';

/**
 * V2LoginCard — Full login card component.
 *
 * Props (from useV2AuthForm spread + explicit):
 * - email, password, showPassword, loading, error, success
 * - setEmail, setPassword, setShowPassword, handleLogin
 * - onCreateAccount, onOktaClick, onForgotPassword
 */
function V2LoginCard({
  email,
  password,
  showPassword,
  loading,
  error,
  success,
  setEmail,
  setPassword,
  setShowPassword,
  handleLogin,
  onCreateAccount,
  onOktaClick,
  onForgotPassword,
}) {
  const { theme } = useV2Theme();
  const cloobotXLogo = theme === 'light' ? cloobotXLogoLight : cloobotXLogoDark;
  const isAccountNotFoundError = error === 'Account Not found' || error === 'Account not found';

  return (
    <div className="v2-login-card">
      <img src={cloobotXLogo} alt="CloobotX" className="v2-login-card__logo" />
      {/* <h1 className="v2-login-card__title">Welcome back</h1> */}
      {/* <p className="v2-login-card__subtitle">Sign in to continue to CloobotX</p> */}

      {error && (
        <div className="v2-login-card__alert v2-login-card__alert--error">
          {isAccountNotFoundError ? (
            <>
              Account not found. Do you want to{' '}
              <button
                type="button"
                className="v2-login-card__alert-link"
                onClick={onCreateAccount}
                disabled={loading}
              >
                Sign up
              </button>
              ?
            </>
          ) : (
            error
          )}
        </div>
      )}
      {success && (
        <div className="v2-login-card__alert v2-login-card__alert--success">{success}</div>
      )}

      <form
        className="v2-login-card__form"
        onSubmit={(e) => { e.preventDefault(); handleLogin(); }}
      >
        <div className="v2-login-card__field">
          <label className="v2-login-card__label" htmlFor="v2-login-email">
            Email <span className="v2-login-card__required">*</span>
          </label>
          <div className="v2-login-card__input-wrap">
            <V2Icon name="mail" className="v2-login-card__input-icon" />
            <input
              id="v2-login-email"
              type="email"
              autoComplete="email"
              autoFocus
              className="v2-login-card__input"
              value={email}
              placeholder="you@company.com"
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
              disabled={loading}
            />
          </div>
        </div>

        <div className="v2-login-card__field">
          <label className="v2-login-card__label" htmlFor="v2-login-password">
            Password <span className="v2-login-card__required">*</span>
          </label>
          <div className="v2-login-card__input-wrap">
            <V2Icon name="lock" className="v2-login-card__input-icon" />
            <input
              id="v2-login-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              className="v2-login-card__input"
              value={password}
              placeholder="••••••••"
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
              disabled={loading}
            />
            <button
              type="button"
              className="v2-login-card__pw-toggle"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              <V2Icon name={showPassword ? 'visibility_off' : 'visibility'} />
            </button>
          </div>
        </div>

        <div className="v2-login-card__row v2-login-card__row--right">
          <button type="button" className="v2-login-card__link" onClick={onForgotPassword}>
            Forgot password?
          </button>
        </div>

        <V2Button
          variant="primary"
          type="submit"
          disabled={loading}
          className="v2-login-card__submit"
        >
          {loading ? 'Signing in...' : 'Sign in'}
        </V2Button>
      </form>

      <div className="v2-login-card__divider"><span>or</span></div>

      <V2Button
        variant="secondary"
        onClick={() => onOktaClick('login')}
        disabled={loading}
        className="v2-login-card__okta-btn"
      >
        <V2Icon name="business" />
        Continue with Okta SSO
      </V2Button>

      <div className="v2-login-card__footer">
        <span>Don&apos;t have an account?</span>
        <V2Button variant="subtle" onClick={onCreateAccount} disabled={loading}>
          Sign up
        </V2Button>
      </div>
    </div>
  );
}

export default V2LoginCard;
